import * as React from 'react';
import { Check, ChevronLeft } from 'lucide-react';
import { Button } from '~/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator,
} from '~/components/ui/primitives';
import { cn } from '~/lib/utils';
import { STEPS } from './booking-types';

/** Presentation commune a toutes les etapes : titre, contenu, navigation. */

export function StepIndicator({ current }: { current: number }) {
  return (
    <nav aria-label="Progression de la reservation">
      <ol className="flex items-center gap-1 overflow-x-auto scroll-thin pb-1">
        {STEPS.map((item, index) => {
          const done = index < current;
          const active = index === current;

          return (
            <li key={item.id} className="flex shrink-0 items-center gap-1">
              <span
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                  active && 'bg-primary text-primary-foreground',
                  done && 'bg-success/15 text-success',
                  !active && !done && 'text-muted-foreground',
                )}
                aria-current={active ? 'step' : undefined}
              >
                {done ? (
                  <Check className="size-3" aria-hidden />
                ) : (
                  <span aria-hidden>{index + 1}</span>
                )}
                {item.label}
              </span>
              {index < STEPS.length - 1 ? (
                <span className="text-muted-foreground/40" aria-hidden>
                  ·
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
      <p className="sr-only">
        Etape {current + 1} sur {STEPS.length} : {STEPS[current]?.label}
      </p>
    </nav>
  );
}

export function StepShell({
  title,
  description,
  children,
  onBack,
  onNext,
  nextLabel = 'Continuer',
  nextDisabled,
  nextLoading,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  nextLoading?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {children}
        <Separator />
        <div className="flex items-center justify-between gap-2">
          {onBack ? (
            <Button variant="ghost" onClick={onBack}>
              <ChevronLeft aria-hidden />
              Retour
            </Button>
          ) : (
            <span />
          )}
          {onNext ? (
            <Button onClick={onNext} disabled={nextDisabled} loading={nextLoading}>
              {nextLabel}
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Etape 1 — Adresse
// ═══════════════════════════════════════════════════════════════════════════════
